<template>
    <div id="report-page">
        <div class="container-fluid pt-2">

        <h1>{{ t("clientsReport-title") }}</h1>
        <filter-bar
            ref="filterBar"
            @changed="loadData"
            :has-branch-filter="canFilterPerBranch"
            :show-labels="true"
            :filters="extraFilters"
        />
        <div class="clear"></div>
        <loader v-if="is('loading')"></loader>
        <div class="container-fluid" v-if="is('ready')">
            <div class="row">
                <div class="col-sm-12" v-if="periods.length">
                    <div class="box chart-holder">
                        <h2>{{ t("clientsReport-headings-active_clients") }}</h2>
                        <p class="text-center">
                            {{ t("clientsReport-active_clients_description", [placesClientsPeriods.churn]) }}
                        </p>
                        <bar-chart
                            :chart-data="clientsChart"
                            :style="myStyles"
                            :options="chartOptions"
                            :height="218"
                            ref="chart"
                        />
                    </div>
                </div>
                <div class="col-sm-12" v-if="periods.length">
                    <div class="box chart-holder">
                        <h2>{{ t("clientsReport-headings-retention_rate") }}</h2>
                        <bar-chart
                            :chart-data="retentionChart"
                            :style="myStyles"
                            :options="chartOptions"
                            :height="218"
                            ref="chart"
                        />
                    </div>
                </div>
                <div class="col-sm-12" v-if="periods.length">
                    <div class="box chart-holder">
                        <h2>{{ t("clientsReport-headings-clients_by_action") }}</h2>
                        <bar-chart
                            :chart-data="clientActions"
                            :style="myStyles"
                            :options="chartOptions"
                            :height="218"
                            ref="chart"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
    </div>
</template>

<script>
import keviApi from "../keviApi";
import FilterBar from "../components/reports/filter-bar";
import MathHelper from "../helpers/Math";
import _ from "lodash";
import BarChart from "../components/charts/barChart";
import { mapState } from "vuex";
import Chart from "chart.js";
import { displayTextInChart } from "../components/charts/plugins/displayTextInChart";
import { placeStartOfDayISO, placeEndOfDayISO } from "../helpers/datetime";

Chart.plugins.register(displayTextInChart);

export default {
    components: {
        FilterBar,
        BarChart,
    },
    props: {
        user: Object,
    },
    data: function () {
        return {
            extraFilters: [],
            ready: false,
            state: "loading",
            periods: [],
            placesClientsPeriods: [],
            myStyles: {
                height: "218px",
                width: "100%",
                position: "relative",
            },
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                tooltips: {
                    callbacks: {
                        label: function (tooltipItems, data) {
                            if (data.datasets[tooltipItems.datasetIndex].isPercentage) {
                                return parseFloat(tooltipItems.yLabel) + "%";
                            }
                            return tooltipItems.yLabel;
                        },
                    },
                },
                scales: {
                    y: {
                        stacked: true,
                    },
                    yAxes: [
                        {
                            id: "L",
                            type: "linear",
                            position: "left",
                            stacked: true,
                            scaleLabel: {
                                display: false,
                            },
                        },
                    ],
                    xAxes: [
                        {
                            stacked: true,
                            scaleLabel: {
                                display: false,
                            },
                        },
                    ],
                },
                plugins: {
                    barTextPlugin: true
                }
            },
        };
    },
    created: function () {
        document.getElementsByTagName("title")[0].innerText =
            this.t("clientsReport-page_title");
    },
    mounted() {
        const filterBar = this.$refs.filterBar;
        filterBar.addFilter("period");
        if (Object.keys(this.$route.query).length === 0) {
            const tz =
                (this.$store.state.place && this.$store.state.place.timezone) ||
                undefined;
            const now = new Date();
            const oneYearAgo = new Date(now);
            oneYearAgo.setFullYear(now.getFullYear() - 1);
            oneYearAgo.setDate(1);

            // Emit place-tz day boundaries so the value round-trips through the
            // period filter without the day shifting when browser/place zones differ.
            const initialFilters = {
                start: placeStartOfDayISO(oneYearAgo, tz),
                end: placeEndOfDayISO(now, tz)
            };
            filterBar.setValue("period", initialFilters);
        }
        if (this.$module("segments")) {
            let options = keviApi.getOptions("segments");
            this.$http(options).then((response) => {
                let segments = response.body;
                this.extraFilters.push({
                    component: "checkbox",
                    label: this.t("clientsReport-filters-client_segment"),
                    name: "client-segments",
                    options: {
                        options: segments.map((seg) => ({
                            value: seg.id,
                            label: seg.name,
                        })),
                        icon: "fa-tag",
                    },
                    removable: true,
                });
            });
        }
        window.setTimeout(() => {
          this.ready = true;
        }, 100);
    },
    computed: {
        clientsChart() {
            return {
                labels: this.periods.map((p) => p.label),
                datasets: [
                    {
                        label: this.t("clientsReport-headings-active_clients"),
                        backgroundColor: "#A7CCC0",
                        yAxisID: "L",
                        data: this.periods.map((p) => p.active_clients),
                    },
                ],
            };
        },
        retentionChart() {
            return {
                labels: this.periods.map((p) => p.label),
                datasets: [
                    {
                        label: this.t("clientsReport-headings-retention_rate"),
                        isPercentage: true,
                        backgroundColor: "#A7CCC0",
                        yAxisID: "L",
                        data: this.periods.map((p) => p.retention_rate),
                    },
                ],
            };
        },
        clientActions() {
            return {
                labels: this.periods.map((p) => p.label),
                datasets: [
                    {
                        label: this.t("clientsReport-actions-retained", [this.placesClientsPeriods.active]),
                        backgroundColor: "#B3CF99",
                        yAxisID: "L",
                        data: this.periods.map((p) => p.retained),
                        stack: "Stack 0",
                    },
                    {
                        label: this.t("clientsReport-actions-reactivated", [this.placesClientsPeriods.active, this.placesClientsPeriods.churn]),
                        backgroundColor: "#87AB69",
                        yAxisID: "L",
                        data: this.periods.map((p) => p.reactivated),
                        stack: "Stack 0",
                    },
                    {
                        label: this.t("clientsReport-actions-recovered", [this.placesClientsPeriods.churn]),
                        backgroundColor: "#4b6043",
                        yAxisID: "L",
                        data: this.periods.map((p) => p.recovered),
                        stack: "Stack 0",
                    },
                    {
                        label: this.t("clientsReport-actions-new_first_purchase"),
                        backgroundColor: "#1333b4",
                        yAxisID: "L",
                        stacked: false,
                        data: this.periods.map((p) => p.new),
                        stack: "Stack 1",
                    },
                ],
            };
        },
        ...mapState("placeDetails", ["users", "roles", "branches"]),
        user() {
            return this.$store.state.user;
        },
        placeId() {
            return this.$store.state.place.id;
        },
        canFilterPerBranch() {
            return !!this.user.permissions["reports"]["options"][
                "global_access"
            ];
        },
        hasBranchFilter() {
            let filters = this.$store.state.ordersReport.filters;
            if (
                Object.keys(filters).includes("branch") &&
                Array.isArray(filters.branch)
            ) {
                return filters.branch.length;
            }
            return false;
        },
    },
    methods: {
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
        is: function (state) {
            return this.state === state;
        },
        setState: function (state) {
            this.state = state;
        },
        loadData: function (filters) {
            if(!this.ready) {
              return;
            }
            console.trace(JSON.stringify(filters));
            this.setState("loading");
            var options = keviApi.getOptions("clients/report", "GET", {
                filters: filters,
            });
            this.$http(options).then((response) => {
                if (response.status === 200) {
                    this.periods = response.data.periods;
                    this.placesClientsPeriods = response.data.places_clients_periods;
                }
                this.setState("ready");
            });
        },
    },
    watch: {
        ready() {
            if (this.ready) {
                this.loadData(this.$refs.filterBar.values);
            }
        }
    },
};
</script>
